import { ROUTES, callAPI } from "../API/index";

// -----------------------------------------------
// Board - Notice
// -----------------------------------------------
export const fetchNoticeList = (params = {}) => {
    /**
     * 공지 게시글 목록 조회
     */
    return callAPI(ROUTES.BOARD.NOTICE.POSTS, 'GET', {}, params);
}

export const fetchNoticePopups = (params = {}) => {
    /**
     * 공지 게시글 팝업 목록 조회
     */
    return callAPI(ROUTES.BOARD.NOTICE.POPUPS, 'GET', {}, params);
}

export const createNotice = (noticeData) => {
    /**
     * 공지 게시글 생성
     */
    return callAPI(ROUTES.BOARD.NOTICE.POSTS, 'POST', noticeData);
}

export const fetchNoticeDetail = (no) => {
    /**
     * 공지 게시글 상세 조회
     */
    return callAPI(ROUTES.BOARD.NOTICE.POST, 'GET', {}, { no });
}

export const updateNotice = (no, noticeData) => {
    /**
     * 공지 게시글 수정
     */
    return callAPI(ROUTES.BOARD.NOTICE.POST, 'PUT', noticeData, { no });
}

export const deleteNotice = (no) => {
    /**
     * 공지 게시글 삭제
     */
    return callAPI(ROUTES.BOARD.NOTICE.POST, 'DELETE', {}, { no });
}

export const uploadNoticeImage = (formData) => {
    /**
     * 공지 게시글 이미지 업로드
     * @param {FormData} formData - 이미지 파일을 포함한 FormData (예: formData.append('image', file))
     */
    return callAPI(ROUTES.BOARD.NOTICE.IMAGE.UPLOAD, 'POST', formData);
}

export const getNoticeImage = async (imageId) => {
    /**
     * 공지 게시글 이미지 조회 (권한 체크 포함)
     * @param {string} imageId - 이미지 ID
     * @returns {Promise<Blob>} 이미지 Blob 데이터
     */
    try {
        console.log('이미지 조회 요청:', { imageId });
        const response = await callAPI(ROUTES.BOARD.NOTICE.IMAGE.GET, 'GET', {}, { imageId });
        
        console.log('이미지 조회 응답:', {
            hasResponse: !!response,
            hasData: !!response?.data,
            responseType: typeof response,
            dataType: response?.data?.constructor?.name,
            headers: response?.headers,
            status: response?.status
        });
        
        // response가 없거나 data가 없는 경우
        if (!response) {
            throw new Error('이미지 응답이 없습니다 (response is null/undefined)');
        }
        
        if (!response.data) {
            throw new Error('이미지 응답 데이터가 없습니다');
        }
        
        // Blob 응답인 경우 response.data가 Blob 객체
        // 에러 응답인 경우 JSON으로 파싱하여 확인
        const contentType = response.headers?.['content-type'] || response.headers?.['Content-Type'];
        if (contentType?.includes('application/json')) {
            const text = await response.data.text();
            const errorData = JSON.parse(text);
            throw new Error(errorData.message || errorData.error || '이미지를 불러올 수 없습니다');
        }
        
        // Blob 객체인지 확인
        if (!(response.data instanceof Blob)) {
            console.warn('응답이 Blob 형식이 아님:', {
                dataType: response.data?.constructor?.name,
                data: response.data
            });
            throw new Error(`응답이 Blob 형식이 아닙니다 (${response.data?.constructor?.name || 'unknown'})`);
        }
        
        console.log('Blob 반환 성공:', {
            imageId,
            blobSize: response.data.size,
            blobType: response.data.type
        });
        
        return response.data; // Blob 객체 반환
    } catch (error) {
        console.error('이미지 조회 실패:', {
            imageId,
            error: error.message || error,
            response: error.response,
            stack: error.stack
        });
        throw error;
    }
}

// -----------------------------------------------
// Board - File
// -----------------------------------------------
export const fetchFileList = (params = {}) => {
    /**
     * 양식 파일 게시글 목록 조회
     */
    return callAPI(ROUTES.BOARD.FILE.POSTS, 'GET', {}, params);
}

export const createFile = (fileData) => {
    /**
     * 양식 파일 게시글 생성
     */
    return callAPI(ROUTES.BOARD.FILE.POSTS, 'POST', fileData);
}

export const fetchFileDetail = (no) => {
    /**
     * 양식 파일 게시글 상세 조회
     */
    return callAPI(ROUTES.BOARD.FILE.POST, 'GET', {}, { no });
}

export const updateFile = (no, fileData) => {
    /**
     * 양식 파일 게시글 수정
     */
    return callAPI(ROUTES.BOARD.FILE.POST, 'PUT', fileData, { no });
}

export const deleteFile = (no) => {
    /**
     * 양식 파일 게시글 삭제
     */
    return callAPI(ROUTES.BOARD.FILE.POST, 'DELETE', {}, { no });
}

export const uploadFile = (formData) => {
    /**
     * 양식 파일 업로드
     * @param {FormData} formData - 파일을 포함한 FormData (예: formData.append('file', file))
     */
    return callAPI(ROUTES.BOARD.FILE.UPLOAD, 'POST', formData);
}

export const downloadFile = async (fileId) => {
    /**
     * 양식 파일 다운로드 (권한 체크 포함)
     * @param {string} fileId - 파일 ID
     * @returns {Promise<Blob>} 파일 Blob 데이터
     */
    try {
        const response = await callAPI(ROUTES.BOARD.FILE.DOWNLOAD, 'GET', {}, { fileId });
        
        if (!response) {
            throw new Error('파일 응답이 없습니다');
        }
        
        if (!response.data) {
            throw new Error('파일 응답 데이터가 없습니다');
        }
        
        // Blob 응답인 경우 response.data가 Blob 객체
        const contentType = response.headers?.['content-type'] || response.headers?.['Content-Type'];
        if (contentType?.includes('application/json')) {
            const text = await response.data.text();
            const errorData = JSON.parse(text);
            throw new Error(errorData.message || errorData.error || '파일을 불러올 수 없습니다');
        }
        
        if (!(response.data instanceof Blob)) {
            throw new Error(`응답이 Blob 형식이 아닙니다 (${response.data?.constructor?.name || 'unknown'})`);
        }
        
        return response.data; // Blob 객체 반환
    } catch (error) {
        console.error('파일 다운로드 실패:', {
            fileId,
            error: error.message || error,
            response: error.response
        });
        throw error;
    }
}